"use client"

import { NewTabLink } from "./new-tab-link"
import Link from "next/link"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SingleChoice } from "./choice"
import { UpcisoCard } from "./upciso-card"
import { CSF_ANSWERS, CSF_CATEGORIES, CSF_CHECK, CSF_FUNCTIONS, csfFocus, csfResults, type CsfAnswer, type CsfFunctionId } from "@/lib/breach/csf"
import { incidentTypeLabel } from "@/lib/breach/intake"
import { setCsfAnswer, type Workspace } from "@/lib/breach/workspace"
import type { GoTo } from "./incident-nav"
import type { WorkspaceUpdater } from "./incident-tools"

const LAST = CSF_CHECK.length + 1 // 0 = focus, 1..6 = questions, 7 = results

export function HardenView({ w, update, page, go }: { w: Workspace; update: WorkspaceUpdater; page: number; go: GoTo }) {
  const p = Math.max(0, Math.min(page, LAST))
  const to = (n: number) => go({ v: "harden", p: Math.max(0, Math.min(n, LAST)) })

  if (p === 0) return <Focus w={w} to={to} go={go} />
  if (p === LAST) return <Results w={w} to={to} go={go} />

  const item = CSF_CHECK[p - 1]
  const fn = CSF_FUNCTIONS.find((f) => f.id === item.fn)!
  const answered = w.csfCheck[item.fn]
  return (
    <section aria-label={`${fn.name} check`} className="max-w-2xl">
      <p className="text-sm text-muted-foreground">Quick check, question {p} of {CSF_CHECK.length}. Nothing here is sent anywhere.</p>
      <div className="mt-2 h-2 rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round((p / CSF_CHECK.length) * 100)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-2 rounded-full bg-accent-solid" style={{ width: `${(p / CSF_CHECK.length) * 100}%` }} />
      </div>
      <h1 className="mt-5 text-2xl font-bold sm:text-3xl">{fn.name}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{fn.plain}</p>
      <h2 className="mt-5 text-lg font-semibold">{item.statement}</h2>
      <div className="mt-4">
        <SingleChoice name={`csf-${item.fn}`} options={CSF_ANSWERS} value={answered ?? null}
          onChange={(v) => update((x) => setCsfAnswer(x, item.fn as CsfFunctionId, v as CsfAnswer))} />
      </div>
      <div className="mt-8 flex items-center justify-between gap-3">
        <Button variant="outline" size="lg" onClick={() => to(p - 1)}><ArrowLeft /> Back</Button>
        {answered
          ? <Button size="lg" className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90" onClick={() => to(p + 1)}>{p === CSF_CHECK.length ? "See my results" : "Next"} <ArrowRight /></Button>
          : <Button size="lg" variant="outline" onClick={() => to(p + 1)}>Skip for now <ArrowRight /></Button>}
      </div>
    </section>
  )
}

function Focus({ w, to, go }: { w: Workspace; to: (n: number) => void; go: GoTo }) {
  const focus = csfFocus(w.intake)
  return (
    <section className="grid max-w-3xl gap-5" aria-label="After the incident">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">After the incident: make it harder next time</h1>
        <p className="mt-2 text-muted-foreground">
          Once things are stable, use what happened to improve your security. We use the{" "}
          <a className="underline" href="https://www.nist.gov/cyberframework" target="_blank" rel="noopener">NIST Cybersecurity Framework 2.0</a>,
          which groups security into six functions: Govern, Identify, Protect, Detect, Respond, and Recover. It&apos;s the same family of guidance as
          the incident response guidance behind this tool.
        </p>
      </div>
      <div>
        <h2 className="text-lg font-semibold">Where your incident points ({incidentTypeLabel(w.intake.incidentType)})</h2>
        <p className="mt-1 text-sm text-muted-foreground">General practice, based on what happened and what you told us. Not a full assessment.</p>
        <ul className="mt-3 grid gap-3">
          {focus.map((f) => (
            <li key={f.category.id} className="rounded-lg border border-border p-4">
              <p className="font-semibold">{f.category.name} <span className="text-xs font-normal text-muted-foreground">({f.category.id})</span></p>
              <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">{f.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <Button size="lg" className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90" onClick={() => to(1)}>Check where you stand (6 quick questions) <ArrowRight /></Button>
        <button className="text-sm underline text-muted-foreground hover:text-foreground" onClick={() => go({ v: "index" })}>Back to my plan</button>
      </div>
    </section>
  )
}

function Results({ w, to, go }: { w: Workspace; to: (n: number) => void; go: GoTo }) {
  const { results, priority, answered } = csfResults(w.csfCheck)
  return (
    <section className="grid max-w-3xl gap-5" aria-label="Your results">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">Where you stand, and what to do first</h1>
        <p className="mt-2 text-sm text-muted-foreground">A rough self-check across the six NIST CSF functions. It isn&apos;t an audit or a certification. {answered < 6 && `You answered ${answered} of 6; you can go back and answer the rest.`}</p>
      </div>
      <ul className="grid gap-2">
        {results.map((r) => (
          <li key={r.fn.id} className="grid grid-cols-[7rem_1fr_6rem] items-center gap-3 rounded-md border border-border p-3 text-sm">
            <span className="font-medium">{r.fn.name}</span>
            <span className="h-2 rounded-full bg-muted" aria-hidden><span className="block h-2 rounded-full bg-accent-solid" style={{ width: `${r.score === null ? 0 : (r.score / 2) * 100}%` }} /></span>
            <span className="text-right text-muted-foreground">{r.answer ? { yes: "Yes", partly: "Partly", no: "No" }[r.answer] : "Not answered"}</span>
          </li>
        ))}
      </ul>
      {priority.length > 0 ? (
        <div>
          <h2 className="text-lg font-semibold">Do these first</h2>
          <ol className="mt-3 grid gap-3">
            {priority.slice(0, 3).map((r, n) => (
              <li key={r.fn.id} className="rounded-lg border border-border p-4">
                <p className="font-semibold">{n + 1}. {r.fn.name} <span className="text-xs font-normal text-muted-foreground">({r.answer === "no" ? "not in place" : "partly in place"})</span></p>
                <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">{r.next.map((x) => <li key={x}>{x}</li>)}</ul>
              </li>
            ))}
          </ol>
        </div>
      ) : answered > 0 ? (
        <p className="rounded-lg border border-border p-4 text-sm text-muted-foreground">You answered &ldquo;Yes&rdquo; to everything you were asked. Keep it current, and test it.</p>
      ) : null}
      <UpcisoCard />
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <Button variant="outline" onClick={() => to(1)}><ArrowLeft /> Review my answers</Button>
        <Button variant="outline" onClick={() => window.print()}>Print</Button>
        <button className="text-sm underline text-muted-foreground hover:text-foreground" onClick={() => go({ v: "index" })}>Back to my plan</button>
        <NewTabLink href="/harden" className="text-sm underline text-muted-foreground hover:text-foreground">About this step</NewTabLink>
      </div>
      <p className="text-xs text-muted-foreground">CSF categories and names are NIST&apos;s. {CSF_CATEGORIES.length} categories are used here at category level only. General information, not legal or compliance advice.</p>
    </section>
  )
}
